type Teardown = () => void

// Tears pages down on two Turbolinks events:
// - `before-cache`: before cloning the page into the snapshot cache.
//   Skipped on `no-cache` pages (see TurbolinksCacheControl).
// - `before-render`: before replacing the <body>; fires on all pages.
//
// Teardowns MUST be idempotent: cacheable pages fire both events.
//
// The events bubble from `document`, so these window listeners run after every
// document-level listener — Vue apps are already unmounted by teardown time.
const PAGE_TEARDOWN_EVENTS = [
  'turbolinks:before-cache',
  'turbolinks:before-render',
] as const

const onPageTeardown = (fn: Teardown, once: boolean): (() => void) => {
  const unsubscribe = (): void => {
    PAGE_TEARDOWN_EVENTS.forEach((eventName) => {
      window.removeEventListener(eventName, handleTeardown)
    })
  }

  const handleTeardown = (): void => {
    if (once) {
      unsubscribe()
    }

    fn()
  }

  PAGE_TEARDOWN_EVENTS.forEach((eventName) => {
    window.addEventListener(eventName, handleTeardown)
  })

  return unsubscribe
}

/**
 * Runs a teardown every time the page is replaced, for the lifetime of the
 * document. Register at module level. The teardown must be idempotent.
 * @param fn - The teardown to run.
 * @returns A function that removes the listeners.
 */
export const onEachPageTeardown = (fn: Teardown): (() => void) =>
  onPageTeardown(fn, false)

/**
 * Runs a teardown the next time the page is replaced, then stops listening.
 * Register from per-page setup code.
 * @param fn - The teardown to run.
 * @returns A function that removes the listeners.
 */
export const onNextPageTeardown = (fn: Teardown): (() => void) =>
  onPageTeardown(fn, true)
